import React from 'react';
import { 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  Typography, 
  Divider,
  Button,
  Chip,
  Paper,
  ButtonGroup
} from '@mui/material';
import { 
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon,
  Diamond as DiamondIcon,
  DoneAll as DoneAllIcon,
  Fingerprint as FingerprintIcon
} from '@mui/icons-material';
import { useAlerts } from '../../contexts/AlertsContext';
import { useAuth } from '../../contexts/AuthContext';

// Helper function to get icon based on alert severity
const getAlertIcon = (severity) => {
  switch (severity) {
    case 'critical':
      return <ErrorIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'info':
      return <InfoIcon />;
    default:
      return <WarningIcon />;
  }
};

// Helper function to get alert type label
const getAlertTypeLabel = (type) => {
  switch (type) {
    case 'PANIC':
      return 'Panic';
    case 'geofence_exit':
      return 'Geofence Exit';
    case 'battery_low':
      return 'Battery Low';
    case 'tampering':
      return 'Tampering';
    case 'movement':
      return 'Movement';
    case 'unusual_movement':
      return 'Unusual Movement';
    case 'speed_alert':
      return 'Speed Alert';
    case 'tamper_detected':
      return 'Tamper Detected';
    default:
      return type;
  }
};

// Helper function to get color based on severity
const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'default';
  }
};

const AlertsListMobile = ({ onSelectAlert }) => {
  const { alerts, getUserAlerts, acknowledgeAlert } = useAlerts();
  const { currentUser } = useAuth();
  
  // Get alerts for the current user
  const userAlerts = currentUser ? getUserAlerts(currentUser.id) : alerts;
  
  // Filter active alerts
  const activeAlerts = userAlerts.filter(alert => alert.status === 'active');
  
  // If no alerts, show a message
  if (activeAlerts.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No active alerts. All your assets are secure.
        </Typography>
      </Box>
    );
  }
  
  const handleAcknowledge = (alertId) => {
    acknowledgeAlert(alertId);
  };
  
  const handleResolve = (alert) => {
    if (onSelectAlert) {
      onSelectAlert(alert);
    }
  };
  
  return (
    <Box sx={{ pb: 7 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Active Alerts ({activeAlerts.length})
          </Typography>
        </Box>
        
        <List disablePadding>
          {activeAlerts.map((alert, index) => (
            <React.Fragment key={alert.id}>
              <ListItem 
                sx={{ 
                  bgcolor: alert.severity === 'critical' ? 'error.50' : 
                          alert.severity === 'warning' ? 'warning.50' : 'info.50',
                  py: 2
                }}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: `${getSeverityColor(alert.severity)}.main`,
                      color: 'white'
                    }}
                  >
                    {getAlertIcon(alert.severity)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
                        {getAlertTypeLabel(alert.type)}
                      </Typography>
                      <Chip 
                        size="small" 
                        label="Active" 
                        color="error"
                        sx={{ ml: 1, height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box component="span">
                      <Typography variant="body2" component="span" sx={{ mt: 0.5, mb: 1, display: 'block' }}>
                        {alert.message}
                      </Typography>
                      
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <DiamondIcon fontSize="small" sx={{ color: 'primary.main', mr: 0.5, fontSize: 16 }} />
                        <Typography variant="caption" component="span" color="text.secondary">
                          {alert.assetName}
                        </Typography>
                      </Box>
                      
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                        <TimeIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5, fontSize: 16 }} />
                        <Typography variant="caption" component="span" color="text.secondary">
                          {new Date(alert.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                      
                      <Box component="span" sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <ButtonGroup size="small" variant="outlined">
                          <Button
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleAcknowledge(alert.id)}
                            sx={{ 
                              borderColor: `${getSeverityColor(alert.severity)}.main`,
                              color: `${getSeverityColor(alert.severity)}.main`
                            }}
                          >
                            Acknowledge
                          </Button>
                          <Button
                            startIcon={<FingerprintIcon />}
                            onClick={() => handleResolve(alert)}
                            sx={{ 
                              borderColor: `${getSeverityColor(alert.severity)}.main`,
                              color: `${getSeverityColor(alert.severity)}.main`
                            }}
                          >
                            Resolve
                          </Button>
                        </ButtonGroup>
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
              {index < activeAlerts.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default AlertsListMobile;
